/** Renders the shell's split tree with react-resizable-panels; each leaf pane is a floating card. */
import type { SplitNode } from "@bundle/core";
import type { ReactNode } from "react";
import { Group, Panel, Separator } from "react-resizable-panels";

type Props = {
  node: SplitNode;
  path?: string;
  renderPane: (paneId: string, tabId: string) => ReactNode;
  onRatio: (path: string, ratio: number) => void;
};

/** Stable structure key so panel defaults re-apply when the tree shape changes. */
function shapeKey(node: SplitNode): string {
  return node.type === "pane" ? node.id : `(${node.split}:${shapeKey(node.a)}|${shapeKey(node.b)})`;
}

export function SplitView({ node, path = "", renderPane, onRatio }: Props) {
  if (node.type === "pane") return <>{renderPane(node.id, node.tabId)}</>;
  const a = `${path}a`;
  const b = `${path}b`;
  return (
    <Group
      key={shapeKey(node)}
      className="split-group"
      orientation={node.split === "row" ? "horizontal" : "vertical"}
      onLayoutChanged={(layout) => {
        const size = layout[a];
        if (size !== undefined) onRatio(path, size / 100);
      }}
    >
      <Panel id={a} defaultSize={`${Math.round(node.ratio * 1000) / 10}%`} minSize="15%" className="split-panel">
        <SplitView node={node.a} path={a} renderPane={renderPane} onRatio={onRatio} />
      </Panel>
      <Separator className={node.split === "row" ? "split-handle split-handle-v" : "split-handle split-handle-h"} />
      <Panel id={b} minSize="15%" className="split-panel">
        <SplitView node={node.b} path={b} renderPane={renderPane} onRatio={onRatio} />
      </Panel>
    </Group>
  );
}
